/**
 * RECEPTION-FIX-AND-POLISH-BATCH-V1 -- FD-20: BOOKING FOLLOWS REGISTRATION
 * (RECEPTION LAW 3, founder-ratified 2026-08-03).
 *
 * The post-register step: register -> NEXT-AVAILABLE offered by default ->
 * the desk confirms -> a future date override is allowed -> "Skip" always
 * stands. Registration NEVER dead-ends and booking is NEVER blocked
 * (R-R6: the offer comes from the SAME server-computed grid the write
 * route enforces -- the walkInOffer selected FROM the grid; availability
 * is never invented).
 *
 * TWO booking legs, ONE writer (POST /api/appointments/booked):
 *  - continue-from-enquiry: the lead leg (journey already reattached);
 *  - plain registration: the FD-20 patient-keyed leg (clinic-scoped).
 *
 * Split by testability (the repo idiom): RegisterBookingOfferView is pure;
 * the container owns the fetch/book state.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { usePatientBookingFields } from './PatientBookingFields';

import {
  bookAppointment,
  bookAppointmentForPatient,
  fetchDaySlots,
  fetchDaySlotsForClinic,
  type BookedAppointmentResult,
  type DaySlotsResponse,
} from '@/dashboard/api';
import { AcButton } from '@/dashboard/ui';
import { clinicToday } from '@/dashboard/ui/day-scope';
import { formatIstDateTime } from '@/lib/datetime-ist';
import {
  DaySlotPicker,
  type DaySlotsState,
} from '@/dashboard/appointments/DaySlotPicker';

export const BOOKING_OFFER_TITLE = 'Registered - book the visit?';
export const BOOKING_OFFER_SKIP = 'Skip - no booking now';
export const BOOKING_OFFER_NONE_TODAY =
  'No open slot left today. Pick another day below, or skip.';
export const BOOKING_OFFER_PICK_ANOTHER = 'Pick another day';
export const BOOKING_OFFER_VIEW_DAY = "View that day's board";

export interface RegisterBookingOfferViewProps {
  readonly bookingPresentation?: boolean;
  readonly slotPresentation?: 'grid' | 'select';
  readonly details?: ReactNode;
  readonly savedDetails?: ReactNode;
  readonly selectionLocked?: boolean;
  readonly bookingDisabled?: boolean;
  readonly retryRequest?: boolean;
  readonly patientName: string;
  /** D4 (RECEPTION-WALK-CORRECTIVE-V1): the header line. Defaults to the
   *  post-registration "<name> is registered - UMR issued." When an EXISTING
   *  patient is booked from search (no fresh registration), the caller passes a
   *  fitting line ("Book a visit for <name>.") so the copy is never a lie. */
  readonly headline?: string;
  /** Today's default offer off the served grid; null = nothing today. */
  readonly offer: { readonly startIso: string; readonly label: string } | null;
  readonly offerLoading: boolean;
  /** The future-date override surface (shown after "Pick another day"). */
  readonly showPicker: boolean;
  readonly onShowPicker?: () => void;
  readonly pickerDate: string;
  readonly onPickerDateChange?: (date: string) => void;
  readonly pickerSlots: DaySlotsState;
  readonly selectedIso: string;
  readonly onSelectIso?: (iso: string) => void;
  readonly onBook?: (startIso: string) => void;
  readonly onSkip?: () => void;
  readonly booking: boolean;
  /** Verbatim server error (ERROR-PASSTHROUGH LAW); null = none. */
  readonly bookError: string | null;
  /** D2 (RECEPTION-WALK-CORRECTIVE-V1): the LANDED UTC instant once the booking
   *  succeeds (null/absent = not booked yet). Drives the explicit landed
   *  clinic-local date+time confirmation so a booking that lands on a different
   *  clinic-day than the desk expected is never silent (the Aug-12 demo). */
  readonly bookedIso?: string | null;
  /** Current saved row, including a later status reached before an exact retry. */
  readonly bookedStatus?: string;
  readonly bookedOutcome?: BookedAppointmentResult['outcome'];
  /** D2: "View that day's board" -- jumps the board to the LANDED clinic-day
   *  (auto-jump is acceptable here: the booking originated on the board). */
  readonly onViewBookedDay?: () => void;
  /** DOCTOR-HANDS-V1 (BUILD 1): the booked-state action's LABEL. The default
   *  is the desk's board jump, which is the right next step when the booking
   *  originated on the board -- and the WRONG one for a doctor mid-consult,
   *  who must not be navigated out of the consultation. Same posture as the
   *  `headline` override above (D4): the caller supplies copy that fits its
   *  own surface, so the button never promises something it does not do. */
  readonly bookedActionLabel?: string;
}

export function RegisterBookingOfferView({
  bookingPresentation = false,
  slotPresentation = 'grid',
  details, savedDetails, selectionLocked = false, bookingDisabled = false, retryRequest = false,
  patientName,
  headline,
  offer,
  offerLoading,
  showPicker,
  onShowPicker,
  pickerDate,
  onPickerDateChange,
  pickerSlots,
  selectedIso,
  onSelectIso,
  onBook,
  onSkip,
  booking,
  bookError,
  bookedIso,
  bookedStatus = 'booked',
  bookedOutcome = 'created',
  onViewBookedDay,
  bookedActionLabel = BOOKING_OFFER_VIEW_DAY,
}: RegisterBookingOfferViewProps): JSX.Element {
  // D2: BOOKED -- state the LANDED clinic-local date + time explicitly, and
  // offer the one action that matters next (see the day it landed on). This
  // replaces the picker once a slot is confirmed: the desk's next question is
  // "did it land where I meant?", answered in words + one jump.
  if (bookedIso != null) {
    const replayLabel: Record<string, string> = {
      booked: 'Already booked',
      confirmed: 'Already confirmed',
      arrived: 'Already arrived',
      checked_in: 'Already checked in',
      cancelled: 'Cancelled appointment',
      no_show: 'No-show appointment',
      completed: 'Completed appointment',
      fulfilled: 'Completed appointment',
      rescheduled: 'Rescheduled appointment',
    };
    const statusLabel = bookedOutcome === 'replayed'
      ? (replayLabel[bookedStatus] ?? 'Saved appointment')
      : bookedStatus === 'arrived' ? 'Arrived' : bookedStatus === 'checked_in' ? 'Checked in' : 'Booked';
    return (
      <div className={bookingPresentation ? "ac-wizard ac-booking-frozen" : "ac-wizard"} data-testid="register-booking-offer">
        <p
          className="ac-wizard__card-title"
          data-testid="booking-offer-booked"
        >
          {bookedOutcome === 'created' ? `${statusLabel} for` : `${statusLabel} ·`}{' '}
          {formatIstDateTime(bookedIso)} (clinic time).
        </p>
        {savedDetails}
        <AcButton
          variant="primary"
          onClick={() => onViewBookedDay?.()}
          data-testid="booking-offer-view-day"
        >
          {bookedActionLabel}
        </AcButton>
      </div>
    );
  }
  return (
    <div className={bookingPresentation ? "ac-wizard ac-booking-frozen" : "ac-wizard"} data-testid="register-booking-offer">
      <p className="ac-wizard__card-title" data-testid="booking-offer-registered">
        {headline ?? `${patientName} is registered - UMR issued.`}
      </p>
      {details}
      {offerLoading ? (
        <p className="ac-wizard__foot-note" data-testid="booking-offer-loading">
          Finding the next available slot...
        </p>
      ) : offer !== null && !showPicker ? (
        <div className="ac-wizard__card" data-testid="booking-offer-next">
          <p className="ac-wizard__card-title">Next available: {offer.label}</p>
          <AcButton
            variant="primary"
            disabled={booking || bookingDisabled}
            onClick={() => onBook?.(offer.startIso)}
            data-testid="booking-offer-book"
          >
            {booking ? 'Booking...' : retryRequest ? 'Retry saved booking' : `Book ${offer.label}`}
          </AcButton>
        </div>
      ) : !showPicker ? (
        <p className="ac-wizard__foot-note" data-testid="booking-offer-none">
          {BOOKING_OFFER_NONE_TODAY}
        </p>
      ) : null}

      {showPicker ? (
        <>
          <DaySlotPicker
            presentation={slotPresentation}
            compact={bookingPresentation}
            date={pickerDate}
            onDateChange={(d) => onPickerDateChange?.(d)}
            slots={pickerSlots}
            selectedIso={selectedIso}
            onSelectIso={(iso) => onSelectIso?.(iso)}
            disabled={booking || selectionLocked}
            idPrefix="register-offer"
          />
          {selectedIso !== '' || bookingPresentation ? (
            <AcButton
              variant="primary"
              disabled={booking || bookingDisabled || selectedIso === ''}
              onClick={() => { if (selectedIso !== '') onBook?.(selectedIso); }}
              data-testid="booking-offer-book-selected"
            >
              {booking ? 'Booking...' : retryRequest ? 'Retry saved booking' : bookingPresentation ? 'Save appointment' : 'Book this slot'}
            </AcButton>
          ) : null}
        </>
      ) : (
        <div className="ac-wizard__quiet-row">
          <button
            type="button"
            className="ac-wizard__quiet"
            disabled={booking || selectionLocked}
            onClick={() => onShowPicker?.()}
            data-testid="booking-offer-pick-another"
          >
            {BOOKING_OFFER_PICK_ANOTHER}
          </button>
        </div>
      )}

      {bookError !== null ? (
        <p className="text-sm text-destructive" role="alert" data-testid="booking-offer-error">
          {bookError}
        </p>
      ) : null}

      <div className="ac-wizard__quiet-row">
        <button
          type="button"
          className="ac-wizard__quiet"
          disabled={booking || selectionLocked}
          onClick={() => onSkip?.()}
          data-testid="booking-offer-skip"
        >
          {bookingPresentation ? 'Back to patient record' : BOOKING_OFFER_SKIP}
        </button>
      </div>
    </div>
  );
}

export interface RegisterBookingOfferProps {
  readonly careAppointmentId?: number;
  readonly careCaseSheetId?: number;
  /** Frozen Reception Booking only; arrival and Doctor keep their established presentation. */
  readonly bookingPresentation?: boolean;
  readonly patientUmr?: string | null;
  /** Explicit appointment booking does not record a physical arrival. */
  readonly bookingIntent?: 'appointment' | 'arrival';
  /** Exact saved arrival from this registration; never a patient-wide latest reason. */
  readonly originWalkinId?: number | null;
  readonly requestedReturn?: { doctorId: number | null; durationMinutes: number | null };
  readonly patientBookingDetails?: boolean;
  readonly onBusyChange?: (busy: boolean) => void;
  readonly patientName: string;
  /** D4: header override for the existing-patient booking (see the View prop). */
  readonly headline?: string;
  readonly patientId: string;
  /** Non-null on the continue-from-enquiry leg (books lead-keyed). */
  readonly leadId: number | null;
  /** The working clinic (patient-leg slots + booking); null = no lawful
   *  clinic -> the offer cannot render and the caller should have skipped. */
  readonly clinicId: number | null;
  /** LAKSHMI-5-BOOK-DOOR-CORRECTIVE-V1: a PROPOSED clinic-day (YYYY-MM-DD)
   *  that opens the date picker prefilled -- the To-schedule queue's
   *  interval intent lands here (propose-then-confirm: the operator still
   *  picks the slot and can change the date; nothing auto-books). Absent or
   *  '' = today's byte-identical default-offer render. */
  readonly initialPickerDate?: string;
  /** D2 (RECEPTION-WALK-CORRECTIVE-V1): now carries the LANDED instant so the
   *  caller can jump the board to the clinic-day the appointment landed on. */
  readonly onBooked: (appointmentId: number, bookedIso: string) => void;
  /** Inform the host as soon as the saved result lands, so its modal title
   *  and Close return reflect the same appointment. */
  readonly onBookingSaved?: (bookedIso: string) => void;
  readonly onSkip: () => void;
  /** BUILD 1: see the View prop -- the booked-state action's label. */
  readonly bookedActionLabel?: string;
  /**
   * DOCTOR-HANDS-V1 (BUILD 1): an alternate FENCED CHANNEL for the two I/O
   * calls, so a surface with its own authorization can REUSE this component
   * instead of cloning it. Omitted (every existing caller) = the desk's own
   * lead/patient legs, byte-identical.
   *
   * WHY A CHANNEL AND NOT A WIDER ROLE: the doctor is denied on the desk's
   * CRM booking route by a frozen role set that config cannot open, and
   * widening that set would have handed her the desk's whole appointment
   * authority. Her door is a separate, narrower act with its own proof
   * (LAW 2 -- per-surface-role-proof), and this prop is how ONE canonical
   * booking component serves both doors: same offer, same slot grid, same
   * propose-then-confirm interaction, different fence behind it.
   */
  readonly bookingChannel?: {
    readonly fetchSlots: (date: string) => Promise<DaySlotsResponse>;
    readonly book: (startIso: string) => Promise<BookedAppointmentResult>;
  };
}

export default function RegisterBookingOffer({
  bookingPresentation = false, patientUmr, careAppointmentId, careCaseSheetId, bookingIntent = 'arrival', originWalkinId,
  patientBookingDetails = false, onBusyChange, requestedReturn,
  patientName,
  headline,
  patientId,
  leadId,
  clinicId,
  initialPickerDate = '',
  onBooked,
  onBookingSaved,
  onSkip,
  bookedActionLabel,
  bookingChannel,
}: RegisterBookingOfferProps): JSX.Element {
  const descriptorEnabled = (patientBookingDetails || careAppointmentId !== undefined) && leadId === null && bookingChannel === undefined;
  const extra = usePatientBookingFields(descriptorEnabled, patientId, clinicId, bookingPresentation ? {umr: patientUmr ?? null} : undefined, careAppointmentId, careCaseSheetId, requestedReturn);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const [today, setToday] = useState<DaySlotsResponse | null>(null);
  const [offerLoading, setOfferLoading] = useState(true);
  // The proposed date seeds the INITIAL state only (mid-edit stability: a
  // live prop can never yank the picker out from under the operator).
  const [showPicker, setShowPicker] = useState(descriptorEnabled || initialPickerDate !== '');
  const [pickerDate, setPickerDate] = useState(initialPickerDate || (descriptorEnabled ? clinicToday() : ''));
  const [pickerSlots, setPickerSlots] = useState<DaySlotsState>({ kind: 'idle' });
  const [selectedIso, setSelectedIso] = useState('');
  const [booking, setBooking] = useState(false);
  const [bookError, setBookError] = useState<string | null>(null);
  // D2: the confirmed booking (landed instant + appointment id). Once set, the
  // offer shows the explicit landed-date confirmation instead of the picker.
  const [booked, setBooked] = useState<{
    appointmentId: number;
    iso: string;
    status: string;
    outcome: BookedAppointmentResult['outcome'];
  } | null>(null);

  useEffect(() => {
    if (extra.attempt) { setShowPicker(true); setPickerDate(clinicToday(new Date(extra.attempt.appointmentTime))); setSelectedIso(extra.attempt.appointmentTime); }
    if (extra.restored) {
      setBooked({appointmentId: extra.restored.appointment.id, iso: extra.restored.appointment.appointmentTime,
        status: extra.restored.appointment.status, outcome: extra.restored.outcome});
      onBookingSaved?.(extra.restored.appointment.appointmentTime);
    }
  }, [extra.attempt, extra.restored, onBookingSaved]);
  useEffect(() => { onBusyChange?.(booking); }, [booking, onBusyChange]);

  useEffect(() => { setBookError(null); if (!extra.attempt) setSelectedIso(''); }, [extra.selectionIdentity, extra.attempt]);

  // The injected channel is held in a REF, and `fetchFor` depends on whether
  // one exists rather than on its object identity. A caller that builds the
  // channel inline (the natural way to write it) hands a NEW object every
  // render; keying the fetch effect on that identity re-fires it forever --
  // an infinite slot-fetch loop that the evidence capture caught. The desk's
  // own legs are unaffected either way: their deps were, and remain,
  // leadId + clinicId.
  const channelRef = useRef(bookingChannel);
  channelRef.current = bookingChannel;
  const hasChannel = bookingChannel !== undefined;
  const fetchFor = useCallback(
    (date: string): Promise<DaySlotsResponse> => {
      const channel = channelRef.current;
      if (channel !== undefined) return channel.fetchSlots(date);
      return leadId !== null
        ? fetchDaySlots(leadId, date)
        : fetchDaySlotsForClinic(clinicId ?? 0, date, extra.slotSelection);
    },
    [hasChannel, leadId, clinicId, extra.slotSelection?.doctorId, extra.slotSelection?.durationMinutes],
  );

  // Today's default offer (empty date = the SERVER resolves the IST day).
  useEffect(() => {
    if (!hasChannel && leadId === null && clinicId === null) {
      setOfferLoading(false);
      return;
    }
    let cancelled = false;
    setOfferLoading(true);
    fetchFor('')
      .then((data) => {
        if (!cancelled) {
          setToday(data);
          setOfferLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setToday(null);
          setOfferLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fetchFor, hasChannel, leadId, clinicId]);

  // The override picker's per-date grid.
  useEffect(() => {
    if (!showPicker || pickerDate === '') {
      setPickerSlots({ kind: 'idle' });
      return;
    }
    let cancelled = false;
    setPickerSlots({ kind: 'loading' });
    fetchFor(pickerDate)
      .then((data) => {
        if (!cancelled) setPickerSlots({ kind: 'ready', data });
      })
      .catch(() => {
        if (!cancelled) setPickerSlots({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [showPicker, pickerDate, fetchFor]);

  const book = (startIso: string): void => {
    if (booking || !extra.ready || !extra.valid) return;
    setBooking(true);
    setBookError(null);
    // B1 (RECEPTION-MONEY-TRUTH-V1, founder-ruled MUST): a patient-leg
    // booking for TODAY'S clinic day from this at-the-desk offer IS the
    // walk-in -- Reception saves Arrived; separate Check in retains its
    // existing registration and identity gates. Other authorized actors
    // keep their existing behavior. A future override stays booked; the
    // clinic-day comparison is THE ONE day-scope convention.
    const isWalkInToday =
      bookingIntent === 'arrival' &&
      leadId === null &&
      clinicToday(new Date(Date.parse(startIso))) === clinicToday();
    const channel = channelRef.current;
    const req =
      descriptorEnabled
        ? extra.book(startIso, isWalkInToday, isWalkInToday ? originWalkinId : null)
        : channel !== undefined
        ? channel.book(startIso)
        : leadId !== null
        ? bookAppointment(leadId, startIso)
        : bookAppointmentForPatient({
            patientId,
            clinicId: clinicId ?? 0,
            appointmentTime: startIso,
            ...(isWalkInToday ? { walkIn: true } : {}),
          });
    req
      .then((res) => {
        if (!alive.current) return;
        setBooking(false);
        // D2: hold the landed instant and confirm it in words before the board
        // opens, so a wrong-day landing is caught HERE, not silently on a board
        // scoped to a different day.
        if (!Number.isFinite(Date.parse(res.appointment.appointmentTime))) {
          setBookError('Saved appointment time unavailable. Refresh and review the patient record.');
          return;
        }
        setBooked({ appointmentId: res.appointment.id,
          iso: res.appointment.appointmentTime,
          status: res.appointment.status,
          outcome: res.outcome });
        onBookingSaved?.(res.appointment.appointmentTime);
      })
      .catch((err: unknown) => {
        if (!alive.current) return;
        setBooking(false);
        setBookError(err instanceof Error ? err.message : 'Booking failed');
      });
  };

  return (
    <RegisterBookingOfferView
      bookingPresentation={bookingPresentation}
      slotPresentation={descriptorEnabled ? 'select' : 'grid'}
      details={extra.fields}
      savedDetails={extra.summary}
      selectionLocked={descriptorEnabled && extra.attempt !== null}
      bookingDisabled={!extra.ready || !extra.valid}
      retryRequest={descriptorEnabled && extra.attempt !== null}
      patientName={patientName}
      headline={headline}
      offer={today?.walkInOffer ?? null}
      offerLoading={offerLoading}
      showPicker={showPicker}
      onShowPicker={() => setShowPicker(true)}
      pickerDate={pickerDate}
      onPickerDateChange={(d) => {
        setPickerDate(d);
        setSelectedIso('');
        setBookError(null);
      }}
      pickerSlots={pickerSlots}
      selectedIso={selectedIso}
      onSelectIso={iso => { setSelectedIso(iso); setBookError(null); }}
      onBook={book}
      onSkip={onSkip}
      booking={booking}
      bookError={bookError}
      bookedIso={booked?.iso ?? null}
      bookedStatus={booked?.status}
      bookedOutcome={booked?.outcome}
      {...(bookedActionLabel !== undefined ? { bookedActionLabel } : {})}
      onViewBookedDay={
        booked !== null
          ? () => { extra.clearConfirmed(); onBooked(booked.appointmentId, booked.iso); }
          : undefined
      }
    />
  );
}
